import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BusyLabel } from "../components/loader/index.js";
import { useToast } from "../components/toast.js";
import { resetPassword } from "../lib/api/index.js";
import { MIN_PASSWORD_LENGTH } from "../lib/password.js";
import { queryKeys } from "../lib/query-keys.js";
import { documentTitle, usePageTitle } from "../lib/routes.js";
import { navigate } from "../lib/url-filters.js";
import { AuthShell } from "./auth-shell.js";

/**
 * Choosing a new password from an emailed link (asked for 2026-10-03).
 *
 * The token is read once and taken out of the address bar straight away, as on
 * the sign-in callback: a live credential in the URL ends up in history and in
 * the next page's Referer. It is kept in memory until the form is sent.
 */
export function ResetPasswordScreen() {
  usePageTitle(documentTitle("Choose a new password"));
  const client = useQueryClient();
  const toast = useToast();
  const [token] = useState(() => {
    const read = new URLSearchParams(window.location.search).get("token");
    if (read !== null) window.history.replaceState(null, "", "/reset-password");
    return read;
  });
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (token === null) {
    return (
      <AuthShell>
        <h1 className="auth-title">That link is missing its token</h1>
        <p className="auth-lede">
          Open the link from the email again, or ask for a new one on the sign-in page.
        </p>
        <a className="button-link auth-password-open" href="/sign-in">
          Back to sign in
        </a>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="auth-title">Choose a new password</h1>
      <p className="auth-lede">
        At least {MIN_PASSWORD_LENGTH} characters. Every other browser signed in to your
        account will be signed out.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          resetPassword(token, password)
            .then(async () => {
              toast.done("Password changed. You are signed in.");
              navigate("/", { replace: true });
              await client.invalidateQueries({ queryKey: queryKeys.me() });
            })
            .catch((error: unknown) => {
              setBusy(false);
              toast.failed("Could not change the password", error);
            });
        }}
      >
        <div className="auth-fields">
          <div className="auth-field">
            <div className="auth-label-row">
              <label className="auth-label" htmlFor="new-password">
                New password
              </label>
              <button
                className="auth-reveal"
                type="button"
                aria-pressed={revealed}
                aria-controls="new-password"
                onClick={() => {
                  setRevealed(!revealed);
                }}
              >
                {revealed ? "Hide" : "Show"}
              </button>
            </div>
            <input
              id="new-password"
              className="text-input"
              type={revealed ? "text" : "password"}
              required
              autoFocus
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>
        </div>
        <button className="button-primary auth-submit" type="submit" disabled={busy}>
          <BusyLabel busy={busy} idle="Save and sign in" working="Saving…" />
        </button>
      </form>
    </AuthShell>
  );
}
